import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Activity, Users, AlertTriangle, XCircle, Building2, Clock, RefreshCw, ShieldAlert, FileWarning, Wifi } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60) return `${diff}s sedan`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min sedan`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h sedan`;
  return `${Math.floor(diff / 86400)}d sedan`;
}

const KpiCard = ({ icon: Icon, label, value, tone, testid }) => {
  const tones = {
    ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200',
    info: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`border rounded-xl p-4 ${tones[tone] || tones.info}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
};

export default function SuperAdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/api/sa/health/dashboard`, { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Kunde inte ladda hälsodata');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchData, 30000); // Refresh var 30:e sekund
    return () => clearInterval(t);
  }, [autoRefresh]);

  const summary = data?.summary || {};

  const failedPlans = data?.failed_material_plans || [];
  const failedSignings = data?.failed_signings || [];
  const onlineUsers = data?.online_users || [];
  const activeCompanies = data?.recently_active_companies || [];

  // Gruppera online per roll för snabb översikt
  const roleBreakdown = useMemo(() => {
    const counts = { admin: 0, worker: 0, superadmin: 0, other: 0 };
    for (const u of onlineUsers) {
      const r = u.role || 'other';
      if (r in counts) counts[r] += 1;
      else counts.other += 1;
    }
    return counts;
  }, [onlineUsers]);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="sa-health-loading">
        Laddar hälsodata...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sa-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="text-[hsl(16,100%,55%)]" />
            Systemhälsa
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Senast uppdaterad: {formatRelative(data?.generated_at)} · Auto-refresh var 30:e sekund
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="toggle-auto-refresh"
            />
            Auto-refresh
          </label>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="px-4 py-2 bg-[hsl(16,100%,55%)] text-white rounded-lg text-sm font-medium hover:bg-[hsl(16,100%,50%)] disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-health-btn"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Uppdatera
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-4" data-testid="health-error">
          {error}
        </div>
      )}

      {/* KPI-grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={Wifi}
          label="Online nu"
          value={summary.online_users || 0}
          tone={summary.online_users > 0 ? 'ok' : 'info'}
          testid="kpi-online"
        />
        <KpiCard
          icon={Users}
          label="Totalt användare"
          value={summary.total_users || 0}
          tone="info"
          testid="kpi-total-users"
        />
        <KpiCard
          icon={Building2}
          label="Företag"
          value={summary.total_companies || 0}
          tone="info"
          testid="kpi-companies"
        />
        <KpiCard
          icon={FileWarning}
          label="Fail. materialplan 24h"
          value={summary.failed_material_plans_24h || 0}
          tone={summary.failed_material_plans_24h > 0 ? 'danger' : 'ok'}
          testid="kpi-failed-plans"
        />
        <KpiCard
          icon={ShieldAlert}
          label="Fail. BankID 24h"
          value={summary.failed_signings_24h || 0}
          tone={summary.failed_signings_24h > 0 ? 'danger' : 'ok'}
          testid="kpi-failed-signings"
        />
        <KpiCard
          icon={Clock}
          label="Pågår just nu"
          value={summary.material_plans_in_progress || 0}
          tone={summary.material_plans_in_progress > 3 ? 'warn' : 'info'}
          testid="kpi-in-progress"
        />
      </div>

      {/* Grid: Online-users + Recently active companies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Online-users */}
        <section className="bg-white border border-border rounded-xl p-5" data-testid="online-users-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              Online just nu ({onlineUsers.length})
            </h2>
            <div className="text-xs text-muted-foreground">
              Admin: {roleBreakdown.admin} · Worker: {roleBreakdown.worker} · SA: {roleBreakdown.superadmin}
            </div>
          </div>
          {onlineUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Ingen är online just nu.</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
              {onlineUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0" data-testid={`online-user-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {u.company_name} · {u.role}
                    </div>
                  </div>
                  <span className="text-xs text-emerald-600 font-medium ml-3 whitespace-nowrap">
                    {formatRelative(u.last_seen_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recently active companies */}
        <section className="bg-white border border-border rounded-xl p-5" data-testid="active-companies-panel">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 size={18} className="text-[hsl(16,100%,55%)]" />
            Aktiva företag senaste timmen
          </h2>
          {activeCompanies.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Inga företag varit aktiva senaste timmen.</p>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
              {activeCompanies.map((c) => (
                <div key={c.company_id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0" data-testid={`active-company-${c.company_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.company_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {c.active_users} användare · senast {formatRelative(c.latest_seen_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Failed material plans */}
      <section className="bg-white border border-border rounded-xl p-5" data-testid="failed-plans-panel">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileWarning size={18} className="text-rose-500" />
          Misslyckade materialplaner senaste 24h ({failedPlans.length})
        </h2>
        {failedPlans.length === 0 ? (
          <p className="text-sm text-emerald-600">Inga misslyckanden — allt rullar på!</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {failedPlans.map((p) => (
              <div key={p.id} className="border border-rose-200 bg-rose-50 rounded-lg p-3 text-sm" data-testid={`failed-plan-${p.id}`}>
                <div className="flex justify-between mb-1">
                  <div className="font-medium">{p.company_name} — {p.job_type || '—'}</div>
                  <div className="text-xs text-muted-foreground">{formatRelative(p.created_at)}</div>
                </div>
                <div className="text-xs text-rose-700">{p.error_message || 'Okänt fel'}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Failed signings */}
      <section className="bg-white border border-border rounded-xl p-5" data-testid="failed-signings-panel">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <ShieldAlert size={18} className="text-rose-500" />
          Misslyckade BankID-signeringar senaste 24h ({failedSignings.length})
        </h2>
        {failedSignings.length === 0 ? (
          <p className="text-sm text-emerald-600">Inga BankID-fel — allt fungerar!</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {failedSignings.map((s) => (
              <div key={s.id} className="border border-rose-200 bg-rose-50 rounded-lg p-3 text-sm" data-testid={`failed-sign-${s.id}`}>
                <div className="flex justify-between mb-1">
                  <div className="font-medium">{s.company_name} — {s.user_email}</div>
                  <div className="text-xs text-muted-foreground">{formatRelative(s.created_at)}</div>
                </div>
                <div className="text-xs text-rose-700">
                  <span className="font-mono bg-white px-1 rounded mr-1">{s.step}</span>
                  {s.error || 'Okänt fel'}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
